<template>
  <div
    v-if="file"
    class="flex flex-col p-12px b-rd-8px bg-white select-none"
  >
    <div class="flex flex-row gap-8px flex-1">
      <template v-if="filetype == AIGenFileType.PDF">
        <Icon
          name="filetype-pdf"
          class="h-64px w-auto"
        />
      </template>
      <template v-else>
        <Image
          preview
          class="illustration shrink-0 m-auto"
        >
          <template #image>
            <img
              :src="imagePreviewUrl"
              class="h-78px w-78px rounded-4px object-cover"
              @click.stop
            />
          </template>
          <template #preview="slotProps">
            <img
              :src="imagePreviewUrl"
              class="object-cover"
              :style="slotProps.style"
              @click="slotProps.onClick"
            />
          </template>
        </Image>
      </template>
      <div class="flex flex-col flex-1">
        <span class="flex-1">{{ file?.name }}</span>
        <div
          v-if="filetype == AIGenFileType.PDF"
          class="flex flex-row gap-2 text-[var(--ld-brand-500)]"
        >
          <span
            v-if="selectedPages.length > 0"
            @click="openPdfPageSelector()"
          >
            {{
              _t('cardview.ai_gen_selected_pages', {
                selectedPagesLength: selectedPages.length,
              })
            }}
          </span>
          <span
            v-else
            @click="openPdfPageSelector()"
          >
            {{ _t('cardview.ai_gen_select_pages') }}
          </span>
        </div>
        <div
          v-if="showUploading"
          class="flex flex-row flex-justify-between text-[var(--text-color-secondary)]"
        >
          <span>{{ uploadedSizeText }}/{{ fileSizeText }}</span>
          <span>{{ _t('cardview.uploading') }}</span>
        </div>
        <div
          v-else
          class="flex flex-row flex-justify-between"
        >
          <span class="text-[var(--text-color-secondary)]">
            {{ fileSizeText }}
          </span>
          <div
            class="flex flex-row gap-2px flex-items-center"
            @click="selectFile"
          >
            <Icon name="reselect"></Icon>
            <span class="text-[var(--ld-brand-500)]">
              {{ _t('cardview.ai_gen_reselect') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="relative position-bottom--4px h-0px">
      <div
        v-if="showUploading"
        class="w-full transition-opacity duration-300"
      >
        <ProgressBar
          :percent="uploadPercent"
          :color="'linear-gradient(90deg, var(--ld-brand-400) 0.01%, var(--ld-brand-500) 99.98%)'"
          :lottie="false"
          :highlight="false"
          :borderRadius="1"
          class="h-4px!"
        />
      </div>
    </div>
  </div>
  <div
    v-else
    :class="[
      'h-78px w-full flex items-center justify-center border border-dashed rounded bg-[var(--surface-100)] cursor-pointer',
      {
        'ml-auto': !_global.isPcMode,
      },
    ]"
    @click="selectFile"
  >
    <span>
      {{
        filetype == AIGenFileType.IMAGE
          ? _t('cardview.ai_gen_select_image')
          : _t('cardview.ai_gen_select_file')
      }}
    </span>
  </div>
</template>

<script lang="ts">
export enum AIGenFileType {
  IMAGE,
  PDF,
}
</script>
<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import Image from 'primevue/image'
import ProgressBar from '@/components/ProgressBar.vue'
import { pickFile } from '@/utils/misc'
import PdfPageSelector from '@/components/pdf/PdfPageSelector.vue'
import { formatBytes } from '@/utils/file'

defineExpose({ selectFile })

const file = defineModel<File | undefined>('file', { required: true })
const selectedPages = defineModel<number[]>('selectedPages', {
  default: [],
})

const props = defineProps<{
  filetype: AIGenFileType
  uploading: boolean // 正在上传的标识符，在template里不直接用这个控制显示，而是用showUploading
  uploadProgress: {
    total: number // 总文件大小，Byte
    loaded: number // 已经上传的，Byte
    rate: number // 速度
  }
}>()

// PDF 相关量
const isPageSelectorOpening = ref(false)
const pdfUrl = ref<string | null>(null)

// 图片相关量
const imagePreviewUrl = ref<string>('')

// 文件上传相关量
const showUploading = ref<boolean>(props.uploading)
// 上传完毕延迟一小会再取消显示进度条
watch(
  () => props.uploading,
  () => {
    if (props.uploading) {
      showUploading.value = true
    } else {
      setTimeout(() => {
        showUploading.value = false
      }, 300)
    }
  }
)
const fileSizeText = computed(() =>
  formatBytes(props.uploadProgress.total || file.value?.size || 0)
)
const uploadPercent = computed(
  () => (props.uploadProgress.loaded / props.uploadProgress.total) * 100
)
const uploadedSizeText = computed(() =>
  formatBytes(props.uploadProgress.loaded)
)

const acceptTypeMap: Record<AIGenFileType, string> = {
  [AIGenFileType.IMAGE]:
    '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp',
  [AIGenFileType.PDF]: '.pdf,application/pdf',
}

async function selectFile() {
  file.value = await pickFile(acceptTypeMap[props.filetype])
  selectedPages.value.length = 0
}

// 打开页码选择器
async function openPdfPageSelector() {
  if (!file.value) return
  if (isPageSelectorOpening.value) return

  // 创建文件URL（如果还没创建）
  if (!pdfUrl.value) {
    pdfUrl.value = URL.createObjectURL(file.value)
  }

  isPageSelectorOpening.value = true
  try {
    await _openDialog(PdfPageSelector, {
      rootClass: 'g-dialog max-h-500px',
      title: '选择页码',
      props: {
        pdfUrl: pdfUrl.value,
        selectedPages: selectedPages.value,
        'onUpdate:selectedPages': (value: number[]) => {
          selectedPages.value = value
        },
      },
    })
  } catch (error) {
    console.error('Error opening page selector:', error)
  } finally {
    isPageSelectorOpening.value = false
  }
}

// 监听文件变化
watch(file, () => {
  switch (props.filetype) {
    case AIGenFileType.IMAGE:
      // 显示图片
      if (file.value) {
        imagePreviewUrl.value = URL.createObjectURL(file.value)
      } else {
        imagePreviewUrl.value = ''
      }
      break
    case AIGenFileType.PDF:
      // 重置状态
      isPageSelectorOpening.value = false
      selectedPages.value.length = 0
      if (pdfUrl.value) {
        URL.revokeObjectURL(pdfUrl.value)
      }
      pdfUrl.value = null
      if (file.value) {
        openPdfPageSelector() // 文件变化时自动打开选择器
      }
  }
})

// 组件卸载时清理URL
onUnmounted(() => {
  if (imagePreviewUrl.value) {
    URL.revokeObjectURL(imagePreviewUrl.value)
  }
  if (pdfUrl.value) {
    URL.revokeObjectURL(pdfUrl.value)
  }
})

onMounted(async () => {
  if (!file.value) {
    await selectFile()
  }
  if (
    props.filetype == AIGenFileType.PDF &&
    selectedPages.value.length == 0 &&
    !isPageSelectorOpening.value
  ) {
    await openPdfPageSelector()
  }
})
</script>
